import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Popover, Card, CardContent, Typography, Button, Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Person as PersonIcon, PhotoCamera as PhotoCameraIcon, Logout as LogoutIcon, Settings as SettingsIcon, Cake as CakeIcon, Work as WorkIcon, Badge as BadgeIcon, Home as HomeIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import { Divider } from '@mui/material';
import { supabase } from '../config/supabaseClient';

const UserCardPopover = ({ user, onLogout, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.profilbild || user?.mitarbeiterDetails?.profilbild || user?.user_metadata?.avatar_url || user?.avatar_url || null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.profilbild || user?.mitarbeiterDetails?.profilbild || user?.user_metadata?.avatar_url || user?.avatar_url) {
      setAvatarUrl(user?.profilbild || user?.mitarbeiterDetails?.profilbild || user?.user_metadata?.avatar_url || user?.avatar_url);
    }
  }, [user]);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleFileChange = async (event) => {
    try {
      setUploading(true);
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Du musst ein Bild auswählen.');
      }
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id || Math.random()}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // 1. Lade das Bild in den Supabase Storage Bucket "avatars" hoch
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      // 2. Hole die öffentliche URL des Bildes
      const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = data.publicUrl;

      // 3. Aktualisiere die user_metadata im Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl }
      });
      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
    } catch (error) {
      console.error('Fehler beim Upload:', error.message);
      alert('Upload fehlgeschlagen. Hast du den Supabase Bucket "avatars" als public erstellt?');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {React.cloneElement(children, { onClick: handleAvatarClick })}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ 
          sx: { 
            borderRadius: 4, 
            minWidth: 320, 
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            mt: 1.5
          } 
        }}
      >
        <Card elevation={0} sx={{ borderRadius: 0 }}>
          {/* Gradient Header */}
          <Box sx={{ 
            height: 100, 
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            position: 'relative'
          }} />
          
          <CardContent sx={{ pt: 0, px: 3, pb: 3, textAlign: 'center' }}>
            {/* Centered Overlapping Avatar with Camera Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -5, mb: 2, position: 'relative' }}>
              <BadgeBox>
                <Avatar 
                  src={avatarUrl}
                  sx={{ 
                    bgcolor: '#f1f5f9', 
                    color: '#94a3b8',
                    width: 88, 
                    height: 88,
                    fontSize: 32,
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    border: '4px solid #fff'
                  }}
                >
                  {!avatarUrl && <PersonIcon sx={{ fontSize: 48 }} />}
                </Avatar>
                
                {/* Upload Button */}
                <Tooltip title="Profilbild ändern" placement="top">
                  <IconButton
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: -5,
                      bgcolor: '#1976d2',
                      color: '#fff',
                      width: 32,
                      height: 32,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#1565c0' },
                      border: '2px solid #fff'
                    }}
                  >
                    {uploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </BadgeBox>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept="image/*"
                onChange={handleFileChange} 
              />
            </Box>

            
            <Typography variant="h6" fontWeight={800} color="#1e293b" sx={{ mb: 0.5 }}>
              {user?.user_metadata?.vorname ? `${user.user_metadata.vorname} ${user.user_metadata.nachname || ''}` : (user?.fullName || 'Administrator')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontWeight: 500 }}>
              {user?.user_metadata?.position || user?.user_metadata?.rolle || 'Mitarbeiter'}
            </Typography>

            <Box sx={{ textAlign: 'left', mb: 3, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#f8fafc', p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WorkIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Rolle/Position:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto' }}>{user?.user_metadata?.rolle || 'Nicht hinterlegt'}</Typography>
              </Box>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BadgeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Personalnummer:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto' }}>{user?.user_metadata?.personalnummer || 'Nicht hinterlegt'}</Typography>
              </Box>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CakeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Geburtsdatum:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto' }}>{user?.user_metadata?.geburtsdatum || 'Nicht hinterlegt'}</Typography>
              </Box>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HomeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Adresse:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto' }}>{user?.user_metadata?.adresse || 'Nicht hinterlegt'}</Typography>
              </Box>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PhoneIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Telefon:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto' }}>{user?.user_metadata?.telefon || 'Nicht hinterlegt'}</Typography>
              </Box>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>Email:</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 'auto', wordBreak: 'break-all', maxWidth: 160 }}>{user?.email || 'Nicht hinterlegt'}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<SettingsIcon />}
                onClick={() => {
                  handleClose();
                  // navigate('/profile') falls gewünscht
                }}
                sx={{ borderRadius: 3, fontWeight: 700, py: 1, textTransform: 'none' }}
              >
                Profileinstellungen
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={() => {
                  handleClose();
                  onLogout();
                }}
                sx={{ 
                  borderRadius: 3, 
                  fontWeight: 700,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(239,68,68,0.2)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(239,68,68,0.3)', transform: 'translateY(-1px)' },
                  transition: 'all 0.2s'
                }}
              >
                Sicher Abmelden
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
};

// Helper component for relative positioning
const BadgeBox = ({ children }) => <Box sx={{ position: 'relative', display: 'inline-flex' }}>{children}</Box>;

export default UserCardPopover;